import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { ResponsiveService } from '@frk/eds-components';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  DropdownOption,
  PerformanceViewOption,
  ToggleOption,
  ToggleProps,
} from '@types';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import { Observable } from 'rxjs';
import { ProfileService } from '@services/profile.service';
export interface PerformanceComponent {
  performanceView: PerformanceViewOption;
  performanceViewOptions: PerformanceViewOption[];
  performanceAsOf: string;
  isSiteIntl?: boolean;
  periodEnd: string;
  calcType: ToggleProps | string;
  productType: ConfigurationId;
  showPerformanceCurrency: boolean;
  performanceCurrencyOptions: string[];
  selectedPerformanceCurrencyOption: string;
  updatePerformanceView: (
    view: PerformanceViewOption,
    periodEnd: string,
    performanceCurrency?: string
  ) => void;
}

export interface PerformanceGroupHeaderParams {
  perfTable: PerformanceComponent;
}

@Component({
  selector: 'ft-performance-group-header',
  templateUrl: './performance-group-header.component.html',
  styleUrls: ['./performance-group-header.component.scss'],
})
export class PerformanceGroupHeaderComponent implements OnChanges {
  @Input() params: PerformanceGroupHeaderParams; // this (using ngOnchanges) allows the switch to be used as a normal component in card view
  ppssPerformanceComponent: PerformanceComponent;
  performanceView: PerformanceViewOption;
  performanceViewOptions: PerformanceViewOption[];
  performanceViewToggle: ToggleOption[] = [];
  performanceCurrencyOptions: DropdownOption[] = [];
  selectedPerformanceCurrencyOption: DropdownOption;
  periodEndValue: string;
  periodEndToggle: ToggleOption[] = [];
  performanceAsOf: string;
  isSiteIntl: boolean;
  selectedPerfTypeHeaderKey: string;
  placement: string;
  isMobile$: Observable<boolean>;
  isHandheld$: Observable<boolean>;
  showAnnualNav = true;
  showPerformanceCurrencyDropdown: boolean;

  constructor(
    private translateService: TranslateService,
    private responsiveService: ResponsiveService,
    private profileService: ProfileService
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
  }

  ngOnChanges(): void {
    if (this.params) {
      this.agInit(this.params);
    }
  }

  agInit(params: PerformanceGroupHeaderParams): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.ppssPerformanceComponent = params.perfTable;
    this.performanceView = this.ppssPerformanceComponent.performanceView;
    if (!this.performanceView) {
      return;
    }
    this.performanceViewOptions = params.perfTable.performanceViewOptions;
    this.performanceAsOf = `${this.translateService.instant(
      'pds.PERF_AS_OF_DATE'
    )} ${this.ppssPerformanceComponent.performanceAsOf}`;

    // NGC-13646: SA - Performance currency calculation
    this.showPerformanceCurrencyDropdown = this.ppssPerformanceComponent.showPerformanceCurrency;

    // NGC-14354: SOUTH AFRICA - DEVICES - PRE - PPSS - Performance Currency drop down duplicate options
    // agInit called multiple times for displaying ppss page on handheld devices. Due to this
    // multiple dropdowns with similar values were created. Solving it here.
    if (this.showPerformanceCurrencyDropdown) {
      this.ppssPerformanceComponent.performanceCurrencyOptions.forEach(
        (currency) => {
          if (
            !this.performanceCurrencyOptions.find(
              (option) => option.value === currency
            )
          ) {
            this.performanceCurrencyOptions.push({
              label:
                currency === 'SHARE_CLASS'
                  ? this.translateService.instant(
                      `products.ppss-performance-currency-share-class-option`
                    )
                  : currency,
              value: currency,
            });
          }
        }
      );
      this.selectedPerformanceCurrencyOption = this.performanceCurrencyOptions.find(
        (currencyoption) =>
          currencyoption.value ===
          this.ppssPerformanceComponent.selectedPerformanceCurrencyOption
      );
    }

    const selectedCalcType =
      typeof this.ppssPerformanceComponent.calcType === 'string'
        ? this.ppssPerformanceComponent.calcType
        : (this.ppssPerformanceComponent.calcType as ToggleProps).selected;
    this.isSiteIntl = this.ppssPerformanceComponent.isSiteIntl;

    // perf type header label (joined by kebab case)
    this.selectedPerfTypeHeaderKey = `${this.performanceViewOptions[0].headerLabelKey}-${selectedCalcType}`;

    // NGC-5376: Make label key different for SMA.
    if (this.ppssPerformanceComponent.productType === ConfigurationId.SMA) {
      this.selectedPerfTypeHeaderKey += '-sma';
    }
    this.placement = `PPSS:${this.ppssPerformanceComponent.productType.replace(
      /-|\s/g,
      ''
    )}TotalReturnsPerformance${upperFirst(camelCase(selectedCalcType))}`;

    this.performanceViewToggle = []; // reset this in case params are updated
    if (this.performanceViewOptions) {
      this.performanceViewOptions.forEach(
        (performanceViewOption: PerformanceViewOption) => {
          this.performanceViewToggle.push({
            active: performanceViewOption.isDefault,
            text: this.translateService.instant(performanceViewOption.labelKey),
            value: performanceViewOption.name,
          });
        }
      );
    }

    if (this.performanceView.periodEnds) {
      this.periodEndToggle = this.performanceView.periodEnds.map(
        (periodEnd) => {
          return {
            text: this.translateService.instant(`products.${periodEnd}`),
            active:
              periodEnd ===
              (this.periodEndValue
                ? this.periodEndValue
                : this.ppssPerformanceComponent.periodEnd),
            value: periodEnd,
          };
        }
      );
    }

    if (
      this.ppssPerformanceComponent.productType === ConfigurationId.VIP &&
      !this.profileService.isLoggedIn()
    ) {
      this.showAnnualNav = false;
    }
  }

  /**
   * Update the performance view as per type selected.
   * @param selectedToggleOption toggle option selected by user
   */
  onPerformanceTypeUpdate(selectedToggleOption: ToggleOption): void {
    this.performanceView = this.performanceViewOptions.find(
      (performanceViewOption: PerformanceViewOption) =>
        performanceViewOption.name === selectedToggleOption.value
    );

    this.ppssPerformanceComponent.updatePerformanceView(
      this.performanceView,
      this.performanceView.periodEnds[0], // set default period end of selected performance type. NGC-3104
      this.showPerformanceCurrencyDropdown
        ? this.selectedPerformanceCurrencyOption.value
        : null
    );
  }

  /**
   * Update the performance view as per type selected.
   * @param selectedToggleOption toggle option selected by user
   */
  onPeriodEndUpdate(selectedToggleOption: ToggleOption): void {
    this.periodEndValue = selectedToggleOption.value;

    this.ppssPerformanceComponent.updatePerformanceView(
      this.performanceView,
      selectedToggleOption.value,
      this.showPerformanceCurrencyDropdown
        ? this.selectedPerformanceCurrencyOption.value
        : null
    );
  }

  /**
   * Update the performance currency as per option selected.
   * @param selectedDropdownOption option selected by user
   */
  onPerformanceCurrencyUpdate(selectedDropdownOption: DropdownOption): void {
    this.selectedPerformanceCurrencyOption = selectedDropdownOption;

    this.ppssPerformanceComponent.updatePerformanceView(
      this.performanceView,
      this.performanceView.periodEnds[0], // set default period end of selected performance type. NGC-3104
      this.selectedPerformanceCurrencyOption.value
    );
  }
}
